import { areMedChronExportUiStatesEqual } from "@/services/userPreferencesService.mapper";
import type { MedChronExportUiState } from "@/services/userPreferencesService.types";
import { useEffect, useState } from "react";

interface UseMedChronExportFormResult {
  formState: MedChronExportUiState | null;
  isDirty: boolean;
  handleFieldChange: (patch: Partial<MedChronExportUiState>) => void;
  handleReset: () => void;
}

/**
 * Local edit-buffer for the export preferences form, seeded once from the fetched `uiState`
 * and diffed against it for dirty-tracking. Owns no save behavior — the caller decides what to
 * do with the edited state.
 */
export function useMedChronExportForm(
  uiState: MedChronExportUiState | undefined
): UseMedChronExportFormResult {
  const [formState, setFormState] = useState<MedChronExportUiState | null>(null);

  // Initialize once the first fetch resolves, guarded on `formState === null` so a background
  // revalidation never clobbers in-progress edits — only Reset should replace the form again.
  useEffect(() => {
    if (uiState && formState === null) {
      setFormState(uiState);
    }
  }, [uiState, formState]);

  const isDirty = Boolean(
    formState && uiState && !areMedChronExportUiStatesEqual(formState, uiState)
  );

  const handleFieldChange = (patch: Partial<MedChronExportUiState>) => {
    setFormState((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handleReset = () => {
    if (uiState) setFormState(uiState);
  };

  return { formState, isDirty, handleFieldChange, handleReset };
}
